/**
 * Cash drawer of this register (backend payments/drawers.py): open with the starting
 * cash, pay in / pay out, close with the cash counted. Every figure is the server's;
 * "expected" is what the drawer should hold now. Paid out and closing ask an owner's /
 * manager's approval code when the owner switched them on (Approval codes).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, ArrowDownCircle, ArrowUpCircle, Lock, Printer, RefreshCw } from 'lucide-react';
import { api, errorText } from '@/apps/dining/lib/api';
import { useApproval } from '@/apps/dining/components/ApprovalDialog';

const money = (c) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);
const toCents = (v) => Math.round((Number(String(v).replace(/[^0-9.]/g, '')) || 0) * 100);
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

function Row({ label, value, strong, testid }) {
  return (
    <div className={`flex justify-between py-1.5 ${strong ? 'text-white font-semibold text-lg border-t border-slate-700 mt-1 pt-2' : 'text-slate-300'}`}>
      <span>{label}</span>
      <span data-testid={testid}>{value}</span>
    </div>
  );
}

function AmountInput({ value, onChange, testid, autoFocus }) {
  return (
    <div className="flex items-center bg-slate-800 border border-slate-700 rounded-xl px-4 h-14">
      <span className="text-slate-400 text-xl mr-2">$</span>
      <input inputMode="decimal" autoFocus={autoFocus} data-testid={testid} value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
        className="bg-transparent text-white text-2xl w-full outline-none" placeholder="0.00" />
    </div>
  );
}

export function DrawerSummary({ drawer }) {
  const closed = drawer.status === 'closed';
  return (
    <div className="space-y-0.5" data-testid="drawer-summary">
      <Row label="Starting cash" value={money(drawer.opening_cents)} />
      <Row label={`Cash sales (${drawer.cash_sales_count})`} value={money(drawer.cash_sales_cents)} testid="drawer-sales" />
      <Row label="Cash tips" value={money(drawer.cash_tips_cents)} />
      <Row label="Paid in" value={money(drawer.paid_in_cents)} />
      <Row label="Paid out" value={`−${money(drawer.paid_out_cents)}`} />
      <Row label={`Cash refunds (${drawer.cash_refunds_count})`} value={`−${money(drawer.cash_refunds_cents)}`} />
      <Row label="Expected in drawer" value={money(drawer.expected_cents)} strong testid="drawer-expected" />
      {closed && (
        <>
          <Row label="Counted" value={money(drawer.counted_cents)} />
          <Row label={drawer.over_short_cents < 0 ? 'Short' : drawer.over_short_cents > 0 ? 'Over' : 'Over / short'}
            value={money(Math.abs(drawer.over_short_cents))} strong testid="drawer-over-short" />
        </>
      )}
    </div>
  );
}

export default function DrawerPage() {
  const [state, setState] = useState({ loading: true, drawer: null, otherOpen: [], error: null });
  const [closedDrawer, setClosedDrawer] = useState(null);
  const [mode, setMode] = useState(null); // paid_in | paid_out | close
  const [name, setName] = useState(() => {
    try { return localStorage.getItem('pos_terminal_id') ? `Register ${localStorage.getItem('pos_terminal_id')}` : 'Register'; } catch (e) { return 'Register'; }
  });
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const approval = useApproval({ inline: true });

  const load = useCallback(async () => {
    try {
      const data = await api.get('/cash-drawers/current');
      setState({ loading: false, drawer: data.drawer, otherOpen: data.other_open || [], error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: errorText(e, 'Could not load the drawer.') }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setMode(null); setAmount(''); setReason(''); setNote(''); setFormError(null); };

  const submit = async (fn) => {
    setBusy(true);
    setFormError(null);
    try {
      const answer = await fn();
      if (answer) return answer;
    } catch (e) {
      setFormError(errorText(e));
    } finally {
      setBusy(false);
    }
    return null;
  };

  const openDrawer = () => submit(async () => {
    const data = await api.post('/cash-drawers/open', { name: name.trim() || 'Register', opening_cents: toCents(amount) });
    setClosedDrawer(null);
    setState((s) => ({ ...s, drawer: data.drawer }));
    reset();
    return data;
  });

  const move = () => submit(async () => {
    if (toCents(amount) <= 0) throw new Error('Enter an amount');
    if (!reason.trim()) throw new Error('Enter a reason');
    const d = state.drawer;
    const data = await approval.run(
      (token) => api.post(`/cash-drawers/${d.id}/moves`, { kind: mode, amount_cents: toCents(amount), reason: reason.trim(), ...(token ? { approval_token: token } : {}) }),
      { action: 'cash.paid_out', entityId: d.id, what: `Pay out ${money(toCents(amount))}` },
    );
    if (data) { setState((s) => ({ ...s, drawer: data.drawer })); reset(); }
    return data;
  });

  const close = () => submit(async () => {
    if (amount === '') throw new Error('Enter the cash you counted');
    const d = state.drawer;
    const data = await approval.run(
      (token) => api.post(`/cash-drawers/${d.id}/close`, { counted_cents: toCents(amount), note: note.trim() || null, ...(token ? { approval_token: token } : {}) }),
      { action: 'cash.close_drawer', entityId: d.id, what: `Close ${d.name}` },
    );
    if (data) { setClosedDrawer(data.drawer); setState((s) => ({ ...s, drawer: null })); reset(); }
    return data;
  });

  if (approval.asking) return <div className="p-6 max-w-lg mx-auto">{approval.dialog}</div>;

  const d = state.drawer;
  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-8" data-testid="drawer-page">
      <style>{'@media print { body * { visibility: hidden; } #drawer-print, #drawer-print * { visibility: visible; color: #000 !important; } #drawer-print { position: absolute; left: 0; top: 0; width: 80mm; } }'}</style>
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Banknote className="w-7 h-7 text-emerald-400" />
          <h1 className="text-2xl font-bold flex-1">Cash drawer</h1>
          <button type="button" onClick={load} className="text-slate-400 hover:text-white p-2" aria-label="Refresh"><RefreshCw className="w-5 h-5" /></button>
        </div>

        {state.loading && <p className="text-slate-400">Loading…</p>}
        {state.error && <p className="text-red-400" data-testid="drawer-error">{state.error}</p>}

        {closedDrawer && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5" id="drawer-print" data-testid="drawer-closed">
            <h2 className="text-lg font-semibold mb-1">{closedDrawer.name} closed</h2>
            <p className="text-sm text-slate-400 mb-3">Opened {when(closedDrawer.opened_at)} by {closedDrawer.opened_by || '—'} · closed {when(closedDrawer.closed_at)} by {closedDrawer.closed_by || '—'}</p>
            <DrawerSummary drawer={closedDrawer} />
            {closedDrawer.moves?.length > 0 && (
              <div className="mt-3 text-sm text-slate-400">
                {closedDrawer.moves.map((m) => (
                  <div key={m.id} className="flex justify-between"><span>{m.kind === 'paid_in' ? 'Paid in' : 'Paid out'}: {m.reason}</span><span>{money(m.amount_cents)}</span></div>
                ))}
              </div>
            )}
            {closedDrawer.close_note && <p className="mt-2 text-sm text-slate-400">Note: {closedDrawer.close_note}</p>}
            <button type="button" onClick={() => window.print()} className="mt-4 w-full h-12 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center gap-2 print:hidden" data-testid="drawer-print-btn">
              <Printer className="w-5 h-5" /> Print close-out
            </button>
          </div>
        )}

        {!state.loading && !d && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4" data-testid="drawer-open-form">
            <p className="text-slate-300">No drawer is open on this register. Count the cash in the drawer and open it before taking cash.</p>
            {state.otherOpen.length > 0 && (
              <p className="text-sm text-slate-400">Open on other registers: {state.otherOpen.map((o) => o.name).join(', ')}</p>
            )}
            <label className="block text-sm text-slate-400">Register name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="drawer-name"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 h-12 text-white outline-none" />
            </label>
            <div className="text-sm text-slate-400">Starting cash</div>
            <AmountInput value={amount} onChange={setAmount} testid="drawer-opening" autoFocus />
            {formError && <p className="text-red-400 text-sm" data-testid="drawer-form-error">{formError}</p>}
            <button type="button" disabled={busy || amount === ''} onClick={openDrawer} data-testid="drawer-open"
              className="w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-semibold text-lg">
              Open drawer
            </button>
          </div>
        )}

        {d && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5" data-testid="drawer-open-view">
            <h2 className="text-lg font-semibold">{d.name}</h2>
            <p className="text-sm text-slate-400 mb-3">Opened {when(d.opened_at)} by {d.opened_by || '—'}</p>
            <DrawerSummary drawer={d} />

            {!mode && (
              <div className="grid grid-cols-3 gap-3 mt-5">
                <button type="button" onClick={() => setMode('paid_in')} data-testid="drawer-paid-in"
                  className="h-20 rounded-xl bg-slate-800 hover:bg-slate-700 flex flex-col items-center justify-center gap-1"><ArrowDownCircle className="w-6 h-6 text-emerald-400" />Paid in</button>
                <button type="button" onClick={() => setMode('paid_out')} data-testid="drawer-paid-out"
                  className="h-20 rounded-xl bg-slate-800 hover:bg-slate-700 flex flex-col items-center justify-center gap-1"><ArrowUpCircle className="w-6 h-6 text-amber-400" />Paid out</button>
                <button type="button" onClick={() => setMode('close')} data-testid="drawer-close"
                  className="h-20 rounded-xl bg-red-600/80 hover:bg-red-600 flex flex-col items-center justify-center gap-1"><Lock className="w-6 h-6" />Close drawer</button>
              </div>
            )}

            {(mode === 'paid_in' || mode === 'paid_out') && (
              <div className="mt-5 space-y-3" data-testid="drawer-move-form">
                <div className="font-semibold">{mode === 'paid_in' ? 'Put cash into the drawer' : 'Take cash out of the drawer'}</div>
                <AmountInput value={amount} onChange={setAmount} testid="drawer-move-amount" autoFocus />
                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} data-testid="drawer-move-reason"
                  placeholder={mode === 'paid_in' ? 'Reason (e.g. change from the bank)' : 'Reason (e.g. ice delivery)'}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 h-12 text-white outline-none" />
                {formError && <p className="text-red-400 text-sm" data-testid="drawer-form-error">{formError}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={reset} className="h-12 rounded-xl bg-slate-800">Back</button>
                  <button type="button" disabled={busy} onClick={move} data-testid="drawer-move-save" className="h-12 rounded-xl bg-emerald-600 disabled:opacity-50 font-semibold">Save</button>
                </div>
              </div>
            )}

            {mode === 'close' && (
              <div className="mt-5 space-y-3" data-testid="drawer-close-form">
                <div className="font-semibold">Count the cash in the drawer</div>
                <AmountInput value={amount} onChange={setAmount} testid="drawer-counted" autoFocus />
                {amount !== '' && (
                  <p className="text-sm text-slate-300" data-testid="drawer-close-preview">
                    {toCents(amount) - d.expected_cents === 0 ? 'Matches the expected cash.'
                      : `${toCents(amount) - d.expected_cents < 0 ? 'Short' : 'Over'} by ${money(Math.abs(toCents(amount) - d.expected_cents))}.`}
                  </p>
                )}
                <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Note (optional)"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 h-12 text-white outline-none" />
                {formError && <p className="text-red-400 text-sm" data-testid="drawer-form-error">{formError}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={reset} className="h-12 rounded-xl bg-slate-800">Back</button>
                  <button type="button" disabled={busy} onClick={close} data-testid="drawer-close-save" className="h-12 rounded-xl bg-red-600 disabled:opacity-50 font-semibold">Close drawer</button>
                </div>
              </div>
            )}

            {d.moves?.length > 0 && !mode && (
              <div className="mt-5 text-sm text-slate-400 space-y-1">
                {d.moves.map((m) => (
                  <div key={m.id} className="flex justify-between"><span>{m.kind === 'paid_in' ? 'Paid in' : 'Paid out'} · {m.reason} · {m.by || ''}</span><span>{money(m.amount_cents)}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
